import { t, type ErrorCodeMapperEntry } from "./types.js";

/** Error codes 14000-14999. One thousand-block per file: a duplicate number here can only
 *  collide within this file, which is what makes the split safer than one shared object. */
export const billingErrors = {
  // 14xxx is the Billing module's block (packages/server/billing/), finally populated. It carries both
  // splits: 14001-14007 came off RESOURCE_NOT_FOUND (9003), 14008-14011 off SOMETHING_WENT_WRONG.
  //
  // ⚠️ Scope note. Only `inHouseBilling.ts` sends these two generics NUMERICALLY. Every other billing
  // file sends them as bare STRINGS (`errorCode: "RESOURCE_NOT_FOUND"`), alongside billing's own
  // string registry (`BILLING_ERROR_CODES`, billing.constant.ts). Those ~29 string sites are NOT
  // covered by the codes below — converting them changes the wire type from string to number, which is
  // the separate billing string→numeric migration. So expect sibling sites with identical messages
  // still answering with strings until that lands.
  //
  // A `billing_customers` row could not be resolved. 5 call sites. NOT
  // BILLING_CUSTOMER_NOT_FOUND_FOR_ACCOUNT (7047) — that one's sentence names the account.
  BILLING_CUSTOMER_NOT_FOUND: {
    errorCode: 14001,
    errorMessage: t("Customer not found"),
    isDynamic: false,
  },
  // A `billing_subscriptions` row could not be resolved.
  BILLING_SUBSCRIPTION_NOT_FOUND: {
    errorCode: 14002,
    errorMessage: t("Subscription not found."),
    isDynamic: false,
  },
  // A `billing_invoices` row could not be resolved.
  BILLING_INVOICE_NOT_FOUND: {
    errorCode: 14003,
    errorMessage: t("Invoice not found."),
    isDynamic: false,
  },
  // A `billing_quotes` row could not be resolved. 3 call sites. Deliberately separate from
  // BILLING_QUOTE_NOT_FOUND (7048), whose message interpolates the quote id ("Quote not found:
  // {{quoteId}}") — reusing it would put an id in front of users who do not see one today.
  BILLING_QUOTE_NOT_FOUND_PLAIN: {
    errorCode: 14004,
    errorMessage: t("Quote not found."),
    isDynamic: false,
  },
  // A `billing_coupons` row could not be resolved for an active coupon code.
  BILLING_COUPON_NOT_FOUND: {
    errorCode: 14005,
    errorMessage: t("Coupon not found"),
    isDynamic: false,
  },
  // A `billing_events` (webhook event) row could not be resolved.
  BILLING_EVENT_NOT_FOUND: {
    errorCode: 14006,
    errorMessage: t("Event not found."),
    isDynamic: false,
  },
  // The coupon exists but its validity window has not started yet.
  BILLING_COUPON_NOT_YET_VALID: {
    errorCode: 14008,
    errorMessage: t("Coupon is not yet valid"),
    isDynamic: false,
  },
  // The coupon has expired. (A string twin `COUPON_EXPIRED` is declared in BILLING_ERROR_CODES but
  // never used; this is the numeric one that is actually sent.)
  BILLING_COUPON_EXPIRED: {
    errorCode: 14009,
    errorMessage: t("Coupon has expired"),
    isDynamic: false,
  },
  // The coupon's redemption limit has been reached. (Same story: an unused string twin exists.)
  BILLING_COUPON_LIMIT_REACHED: {
    errorCode: 14010,
    errorMessage: t("Coupon redemption limit reached"),
    isDynamic: false,
  },
  // `convertQuoteToSubscription`: the quote is expired, void or declined, so it can no longer be
  // converted — reachable from a stale quote link.
  BILLING_QUOTE_NO_LONGER_VALID: {
    errorCode: 14011,
    errorMessage: t("This quote is no longer valid"),
    isDynamic: false,
  },
  // 14012-14029 replace the STRING values `BILLING_ERROR_CODES` (billing/billing.constant.ts) used to
  // own. The constant object is kept and only its values moved here: every consumer references
  // `BILLING_ERROR_CODES.X`, never a duplicated literal, so no call site changed. Six of its members
  // point at codes already declared above (CUSTOMER/SUBSCRIPTION/INVOICE/COUPON_NOT_FOUND,
  // COUPON_EXPIRED, COUPON_LIMIT_REACHED) rather than getting duplicates.
  // Entries for the codes that replaced billing's string registry. Every one of these call sites still
  // sends its own `errorMessage`, so these are the FALLBACK a code-driven client resolves — which is
  // the whole reason the strings were migrated: a string code could never be looked up here at all.
  // Several are umbrella codes whose sites word things differently (PLAN_NOT_FOUND has six wordings),
  // and eight interpolate a runtime value inline; the sentence below is the neutral form for those.
  BILLING_PLAN_NOT_FOUND: {
    errorCode: 14012,
    errorMessage: t("Plan not found"),
    isDynamic: false,
  },
  BILLING_ADDON_NOT_FOUND: {
    errorCode: 14013,
    errorMessage: t("Addon not found"),
    isDynamic: false,
  },
  BILLING_PAYMENT_NOT_FOUND: {
    errorCode: 14014,
    errorMessage: t("Payment not found"),
    isDynamic: false,
  },
  BILLING_PARTNER_NOT_FOUND: {
    errorCode: 14015,
    errorMessage: t("Partner not found"),
    isDynamic: false,
  },
  BILLING_PARTNER_CODE_EXISTS: {
    errorCode: 14016,
    errorMessage: t("A partner with this code already exists"),
    isDynamic: false,
  },
  BILLING_INVALID_PAYMENT_SIGNATURE: {
    errorCode: 14017,
    errorMessage: t("Payment verification failed"),
    isDynamic: false,
  },
  BILLING_PAYMENT_FAILED: {
    errorCode: 14018,
    errorMessage: t("Payment failed"),
    isDynamic: false,
  },
  BILLING_SUBSCRIPTION_ALREADY_CANCELLED: {
    errorCode: 14019,
    errorMessage: t("Subscription is already cancelled"),
    isDynamic: false,
  },
  BILLING_SUBSCRIPTION_NOT_ACTIVE: {
    errorCode: 14020,
    errorMessage: t("Subscription is not active"),
    isDynamic: false,
  },
  BILLING_INVOICE_ALREADY_PAID: {
    errorCode: 14021,
    errorMessage: t("Invoice is already paid"),
    isDynamic: false,
  },
  BILLING_INSUFFICIENT_SEATS: {
    errorCode: 14022,
    errorMessage: t("Not enough seats are available"),
    isDynamic: false,
  },
  BILLING_STORAGE_LIMIT_EXCEEDED: {
    errorCode: 14023,
    errorMessage: t("Storage limit exceeded"),
    isDynamic: false,
  },
  BILLING_FEATURE_NOT_AVAILABLE: {
    errorCode: 14024,
    errorMessage: t("This feature is not available on your plan"),
    isDynamic: false,
  },
  BILLING_INVALID_QUANTITY: {
    errorCode: 14025,
    errorMessage: t("Invalid quantity"),
    isDynamic: false,
  },
  BILLING_PRORATION_ERROR: {
    errorCode: 14026,
    errorMessage: t("Proration could not be calculated"),
    isDynamic: false,
  },
  BILLING_PAYMENT_VERIFICATION_FAILED: {
    errorCode: 14027,
    errorMessage: t("Payment verification failed"),
    isDynamic: false,
  },
  BILLING_BILLED_BY_PARTNER: {
    errorCode: 14028,
    errorMessage: t("Billing details are managed by your channel partner and cannot be edited here"),
    isDynamic: false,
  },
  BILLING_INVALID_GSTIN: {
    errorCode: 14029,
    errorMessage: t("Invalid GSTIN format"),
    isDynamic: false,
  },
  // 14030-14053 replace the ad-hoc inline string literals that were scattered across the billing
  // services and never added to BILLING_ERROR_CODES at all — the messy half of this migration, since
  // each one needed a real call-site edit rather than a values-only flip. The former inline
  // "QUOTE_NOT_FOUND" literal is NOT here: it reuses BILLING_QUOTE_NOT_FOUND_PLAIN (14004).
  BILLING_PAYMENT_METHOD_NOT_FOUND: {
    errorCode: 14030,
    errorMessage: t("Payment method not found"),
    isDynamic: false,
  },
  BILLING_REFUND_EXCEEDS_AVAILABLE: {
    errorCode: 14032,
    errorMessage: t("Maximum refund amount is {{maxAmount}}"),
    isDynamic: true,
    dynamicParams: ["maxAmount"],
  },
  BILLING_INVALID_AMOUNT: {
    errorCode: 14033,
    errorMessage: t("Payment amount must be greater than 0"),
    isDynamic: false,
  },
  BILLING_QUOTE_ALREADY_CONVERTED: {
    errorCode: 14034,
    errorMessage: t("Quote has already been converted to a subscription"),
    isDynamic: false,
  },
  BILLING_INVOICE_NOT_SENDABLE: {
    errorCode: 14037,
    errorMessage: t('Cannot send invoice — current status is "{{status}}"'),
    isDynamic: true,
    dynamicParams: ["status"],
  },
  BILLING_INVOICE_NUMBER_EXISTS: {
    errorCode: 14038,
    errorMessage: t("Invoice number already exists"),
    isDynamic: false,
  },
  BILLING_INVOICE_VOID: {
    errorCode: 14039,
    errorMessage: t("Cannot edit a voided invoice"),
    isDynamic: false,
  },
  BILLING_INVOICE_PAID: {
    errorCode: 14040,
    errorMessage: t("Cannot change status of a paid invoice"),
    isDynamic: false,
  },
  BILLING_PDF_GENERATION_ERROR: {
    errorCode: 14041,
    errorMessage: t("Failed to generate PDF"),
    isDynamic: false,
  },
  BILLING_ADDON_CODE_EXISTS: {
    errorCode: 14042,
    errorMessage: t("Addon with code {{addonCode}} already exists"),
    isDynamic: true,
    dynamicParams: ["addonCode"],
  },
  BILLING_ADDON_HAS_ACTIVE_SUBSCRIPTIONS: {
    errorCode: 14043,
    errorMessage: t("Cannot deactivate addon with {{count}} active subscriptions"),
    isDynamic: true,
    dynamicParams: ["count"],
  },
  BILLING_ADDON_ALREADY_EXISTS: {
    errorCode: 14044,
    errorMessage: t("This addon is already added to the subscription"),
    isDynamic: false,
  },
  BILLING_PLAN_CODE_EXISTS: {
    errorCode: 14045,
    errorMessage: t("Plan with code {{planCode}} already exists"),
    isDynamic: true,
    dynamicParams: ["planCode"],
  },
  BILLING_PLAN_HAS_ACTIVE_SUBSCRIPTIONS: {
    errorCode: 14046,
    errorMessage: t("Cannot archive plan with {{count}} active subscriptions"),
    isDynamic: true,
    dynamicParams: ["count"],
  },
  BILLING_COUPON_CODE_EXISTS: {
    errorCode: 14047,
    errorMessage: t("Coupon with code {{couponCode}} already exists"),
    isDynamic: true,
    dynamicParams: ["couponCode"],
  },
  BILLING_SUBSCRIPTION_ALREADY_EXISTS: {
    errorCode: 14048,
    errorMessage: t("Portal already has an active subscription"),
    isDynamic: false,
  },
  BILLING_UPGRADE_REQUIRES_PAYMENT: {
    errorCode: 14049,
    errorMessage: t("Upgrades have an immediate charge and cannot be scheduled directly"),
    isDynamic: false,
  },
  BILLING_SUBSCRIPTION_ALREADY_PAUSED: {
    errorCode: 14050,
    errorMessage: t("Subscription is already paused"),
    isDynamic: false,
  },
  BILLING_SUBSCRIPTION_NOT_PAUSED: {
    errorCode: 14051,
    errorMessage: t("Subscription is not paused"),
    isDynamic: false,
  },
  BILLING_UPDATE_FAILED: {
    errorCode: 14052,
    errorMessage: t("Failed to update subscription"),
    isDynamic: false,
  },
  BILLING_INVALID_PARTNER_DATA: {
    errorCode: 14053,
    errorMessage: t("Legacy partner has no valid email addresses"),
    isDynamic: false,
  },
  // 14054-14077 completes the billing module's split. Thirteen further sites needed no new code:
  // they reused BILLING_INVOICE_NOT_FOUND, BILLING_SUBSCRIPTION_NOT_FOUND, BILLING_PAYMENT_NOT_FOUND,
  // BILLING_QUOTE_NOT_FOUND_PLAIN and TRIAL_SUBSCRIPTION_MANAGEMENT_BLOCKED, all modulo a trailing
  // period.
  BILLING_SUBSCRIPTION_NO_CUSTOMER: {
    errorCode: 14054,
    errorMessage: t("Subscription has no customer"),
    isDynamic: false,
  },
  BILLING_SUBSCRIPTION_NO_LINE_ITEMS: {
    errorCode: 14055,
    errorMessage: t("Subscription has no line items"),
    isDynamic: false,
  },
  BILLING_RENEWAL_PROFORMA_NO_LINE_ITEMS: {
    errorCode: 14056,
    errorMessage: t("Renewal proforma has no line items"),
    isDynamic: false,
  },
  BILLING_QUOTE_NO_PORTAL_ID: {
    errorCode: 14057,
    errorMessage: t("Quote does not have a portalId. Cannot convert to subscription."),
    isDynamic: false,
  },
  BILLING_QUOTE_NO_PLAN: {
    errorCode: 14058,
    errorMessage: t("No plan found in quote"),
    isDynamic: false,
  },
  BILLING_QUOTE_NO_VALID_PLAN_IN_LINE_ITEMS: {
    errorCode: 14059,
    errorMessage: t("No valid plan found in quote line items"),
    isDynamic: false,
  },
  BILLING_SUBSCRIPTION_NO_RESULT_PAYLOAD: {
    errorCode: 14060,
    errorMessage: t("Subscription was created without a result payload"),
    isDynamic: false,
  },
  BILLING_RENEWAL_PROFORMA_NO_VALID_PLAN: {
    errorCode: 14061,
    errorMessage: t("No valid plan found in renewal proforma"),
    isDynamic: false,
  },
  BILLING_INVOICE_PDF_DOWNLOAD_FAILED: {
    errorCode: 14062,
    errorMessage: t("Could not download the invoice PDF."),
    isDynamic: false,
  },
  BILLING_INVOICE_ONLY_DRAFT_APPROVABLE: {
    errorCode: 14063,
    errorMessage: t("Only draft invoices can be approved"),
    isDynamic: false,
  },
  BILLING_SUBSCRIPTION_NOT_LINKED_TO_PORTAL: {
    errorCode: 14064,
    errorMessage: t("Subscription is not linked to a portal"),
    isDynamic: false,
  },
  BILLING_SUBSCRIPTION_NO_PLAN_CODE: {
    errorCode: 14065,
    errorMessage: t("Subscription has no plan code"),
    isDynamic: false,
  },
  BILLING_INVOICE_NOT_LINKED_TO_SUBSCRIPTION: {
    errorCode: 14066,
    errorMessage: t("Invoice is not linked to a subscription"),
    isDynamic: false,
  },
  BILLING_RAZORPAY_UNAVAILABLE: {
    errorCode: 14069,
    errorMessage: t("Razorpay order creation is not available."),
    isDynamic: false,
  },
  BILLING_INVOICE_NO_TOTAL_FOR_REVERSAL: {
    errorCode: 14070,
    errorMessage: t("Invoice carries no total, so this payment cannot be reversed against it"),
    isDynamic: false,
  },
  BILLING_ONLINE_PAYMENT_RECORD_NOT_FOUND: {
    errorCode: 14071,
    errorMessage: t("Online payment record not found"),
    isDynamic: false,
  },
  BILLING_NO_ACTIVE_SUBSCRIPTION: {
    errorCode: 14072,
    errorMessage: t("No active subscription found"),
    isDynamic: false,
  },
  BILLING_PORTAL_NO_ACCESS: {
    errorCode: 14073,
    errorMessage: t("You do not have access to this portal"),
    isDynamic: false,
  },
  BILLING_NO_SCHEDULED_CANCELLATION: {
    errorCode: 14074,
    errorMessage: t("No scheduled cancellation to remove"),
    isDynamic: false,
  },
  BILLING_SUBSCRIPTION_NO_NUMBER: {
    errorCode: 14075,
    errorMessage: t("Existing subscription has no subscription number"),
    isDynamic: false,
  },
  BILLING_TRIAL_NOT_APPROVABLE: {
    errorCode: 14076,
    errorMessage: t("Only trial_requested or trial_rejected subscriptions can be approved"),
    isDynamic: false,
  },
  BILLING_TRIAL_NOT_REJECTABLE: {
    errorCode: 14077,
    errorMessage: t("Only trial_requested subscriptions can be rejected"),
    isDynamic: false,
  },
  // 14078-14080 came off NO_CHANGES_APPLIED (9005). (14079 held
  // BILLING_SUBSCRIPTION_NOT_FOUND_OR_CANCELLED, removed with staging's cancelSubscription
  // relocation; the number is free.)
  BILLING_SUBSCRIPTION_NOT_FOUND_OR_PAUSED: {
    errorCode: 14078,
    errorMessage: t("Subscription not found, or it is already paused."),
    isDynamic: false,
  },
  // 14082-14107 is the billing module's VALIDATION_FAILED (9004) split. (14081 held
  // BILLING_CUSTOMER_ID_INVALID, removed 2026-09-18 as an unused duplicate of
  // ACCOUNT_CUSTOMER_ID_INVALID (7121); the number is free.) Billing is the heaviest
  // user of 9004 — it validates a lot of ids at the controller boundary. BILLING_INVOICE_ID_REQUIRED
  // (8 sites) and BILLING_PAYMENT_ID_REQUIRED (5) are the two repeated ones.
  BILLING_PLAN_REQUIRED_IN_LINE_ITEMS: {
    errorCode: 14082,
    errorMessage: t("At least one plan is required in lineItems"),
    isDynamic: false,
  },
  // Staging's CUBE AI branch of the same guard (CP-1361). A separate code, not a re-wording of
  // 14082: a subscription carrying ONLY CUBE AI seats needs a product plan, which is a different
  // instruction from "add a plan".
  BILLING_CUBE_AI_NEEDS_PRODUCT_PLAN: {
    errorCode: 14080,
    errorMessage: t("CUBE AI seats need a product plan (CDE, PM or DT) on the subscription. Add at least one."),
    isDynamic: false,
  },
  BILLING_PLAN_OR_LINE_ITEMS_REQUIRED: {
    errorCode: 14083,
    errorMessage: t("Either plan or lineItems is required"),
    isDynamic: false,
  },
  BILLING_SUBSCRIPTION_ID_MISSING: {
    errorCode: 14084,
    errorMessage: t("Subscription is missing an id"),
    isDynamic: false,
  },
  BILLING_CONTACT_EMAIL_REQUIRED: {
    errorCode: 14085,
    errorMessage: t("email is required to create a contact person"),
    isDynamic: false,
  },
  BILLING_EXISTING_SUBSCRIPTION_ID_MISSING: {
    errorCode: 14086,
    errorMessage: t("Existing subscription is missing an id"),
    isDynamic: false,
  },
  BILLING_INVOICE_ID_REQUIRED: {
    errorCode: 14088,
    errorMessage: t("Invoice id is required"),
    isDynamic: false,
  },
  BILLING_QUERY_PARAMS_INVALID: {
    errorCode: 14089,
    errorMessage: t("Invalid query parameters"),
    isDynamic: false,
  },
  BILLING_EMAIL_QUERY_REQUIRED: {
    errorCode: 14090,
    errorMessage: t("A valid email query parameter is required"),
    isDynamic: false,
  },
  BILLING_PARTNER_ID_REQUIRED: {
    errorCode: 14091,
    errorMessage: t("A partner id is required"),
    isDynamic: false,
  },
  BILLING_LEGACY_PARTNER_ID_REQUIRED: {
    errorCode: 14092,
    errorMessage: t("legacyPartnerId is required"),
    isDynamic: false,
  },
  BILLING_PAYMENT_ID_REQUIRED: {
    errorCode: 14093,
    errorMessage: t("paymentId is required"),
    isDynamic: false,
  },
  BILLING_QUOTE_ID_REQUIRED: {
    errorCode: 14095,
    errorMessage: t("quoteId is required"),
    isDynamic: false,
  },
  BILLING_ONLINE_PAYMENT_ID_REQUIRED: {
    errorCode: 14098,
    errorMessage: t("onlinePaymentId is required"),
    isDynamic: false,
  },
  BILLING_ADDON_LINE_ITEM_INCOMPLETE: {
    errorCode: 14099,
    errorMessage: t("Addon line item is missing its code or item id"),
    isDynamic: false,
  },
  // 14101 held BILLING_ACTION_INVALID, removed with the Razorpay payment path; the number is free.
  BILLING_PAYMENT_SIGNATURE_INVALID: {
    errorCode: 14100,
    errorMessage: t("Invalid payment signature"),
    isDynamic: false,
  },
  BILLING_LINE_ITEMS_REQUIRED: {
    errorCode: 14102,
    errorMessage: t("Line items are required"),
    isDynamic: false,
  },
  BILLING_DATE_RANGE_REQUIRED: {
    errorCode: 14103,
    errorMessage: t("fromDate and toDate are required"),
    isDynamic: false,
  },
  BILLING_MONTH_RANGE_REQUIRED: {
    errorCode: 14104,
    errorMessage: t("startMonth and endMonth are required"),
    isDynamic: false,
  },
  BILLING_ADDON_ID_REQUIRED: {
    errorCode: 14105,
    errorMessage: t("addonId is required"),
    isDynamic: false,
  },
  BILLING_QUOTE_AND_PAYMENT_METHOD_REQUIRED: {
    errorCode: 14106,
    errorMessage: t("quoteId and paymentMethod are required"),
    isDynamic: false,
  },
  BILLING_PLAN_NOT_FOUND_BY_CODE: {
    errorCode: 14108,
    errorMessage: t("Plan {{planCode}} not found"),
    isDynamic: true,
    dynamicParams: ["planCode"],
  },
  BILLING_PLAN_NOT_FOUND_FOR_ITEM: {
    errorCode: 14109,
    errorMessage: t("Plan not found for item: {{itemId}}"),
    isDynamic: true,
    dynamicParams: ["itemId"],
  },
  BILLING_QUOTE_LINE_ITEM_INCOMPLETE: {
    errorCode: 14110,
    errorMessage: t("Quote line item is incomplete for item: {{itemId}}"),
    isDynamic: true,
    dynamicParams: ["itemId"],
  },
  BILLING_QUOTE_STATUS_INVALID: {
    errorCode: 14111,
    errorMessage: t("Invalid status. Must be one of: {{validStatuses}}"),
    isDynamic: true,
    dynamicParams: ["validStatuses"],
  },
  BILLING_PARTNER_CODE_EXISTS_NAMED: {
    errorCode: 14136,
    errorMessage: t("Partner with code {{partnerCode}} already exists"),
    isDynamic: true,
    dynamicParams: ["partnerCode"],
  },
  // 14130-14135 carry {{placeholder}} templates: the backend sends the runtime values in `params`
  // and the CLIENT interpolates + translates in one step, instead of the backend authoring prose.
  BILLING_CYCLE_NOT_OFFERED_FOR_PLAN: {
    errorCode: 14130,
    errorMessage: t("Billing cycle '{{billingCycle}}' is not offered for plan {{planCode}}"),
    isDynamic: true,
    dynamicParams: ["billingCycle", "planCode"],
  },
  BILLING_PLAN_NO_PRICE_BRACKETS_NAMED: {
    errorCode: 14131,
    errorMessage: t("Plan {{planCode}} has no price brackets configured"),
    isDynamic: true,
    dynamicParams: ["planCode"],
  },
  BILLING_PLAN_QUANTITY_OUT_OF_RANGE: {
    errorCode: 14132,
    errorMessage: t("Quantity for plan {{planCode}} must be between {{minUnits}} and {{maxUnits}}"),
    isDynamic: true,
    dynamicParams: ["planCode", "minUnits", "maxUnits"],
  },
  BILLING_ADDON_CYCLE_NOT_ALLOWED: {
    errorCode: 14133,
    errorMessage: t("Add-ons cannot be purchased with the {{billingCycle}} billing cycle for plan {{planCode}}"),
    isDynamic: true,
    dynamicParams: ["billingCycle", "planCode"],
  },
  BILLING_ADDON_NOT_FOUND_BY_ID: {
    errorCode: 14134,
    errorMessage: t("Addon {{addonId}} not found"),
    isDynamic: true,
    dynamicParams: ["addonId"],
  },
  BILLING_QUANTITY_OUT_OF_RANGE: {
    errorCode: 14135,
    errorMessage: t("Quantity must be between {{minUnits}} and {{maxUnits}}"),
    isDynamic: true,
    dynamicParams: ["minUnits", "maxUnits"],
  },
  // 14114-14129: each carries VERBATIM the sentence its call site used to send inline, so splitting
  // these off the billing generics changed nothing a user sees.
  BILLING_CUSTOMER_NOT_FOUND_FOR_PORTAL: {
    errorCode: 14114,
    errorMessage: t("No billing customer found for this portal"),
    isDynamic: false,
  },
  BILLING_INVOICE_PAID_CANNOT_VOID: {
    errorCode: 14115,
    errorMessage: t("Cannot void a paid invoice"),
    isDynamic: false,
  },
  BILLING_PARTNER_NOT_FOUND_FOR_EMAIL: {
    errorCode: 14116,
    errorMessage: t("Partner not found for this email"),
    isDynamic: false,
  },
  BILLING_LEGACY_PARTNER_NOT_FOUND: {
    errorCode: 14117,
    errorMessage: t("Legacy partner not found"),
    isDynamic: false,
  },
  BILLING_PAYMENT_NOT_FOUND_OR_INCOMPLETE: {
    errorCode: 14118,
    errorMessage: t("Payment not found or not completed"),
    isDynamic: false,
  },
  BILLING_INVOICE_FULLY_PAID: {
    errorCode: 14119,
    errorMessage: t("Invoice is already fully paid"),
    isDynamic: false,
  },
  BILLING_MANUAL_PAYMENT_NOT_FOUND: {
    errorCode: 14120,
    errorMessage: t("Manual payment not found"),
    isDynamic: false,
  },
  BILLING_PAYMENT_SIGNATURE_VERIFICATION_FAILED: {
    errorCode: 14121,
    errorMessage: t("Payment signature verification failed"),
    isDynamic: false,
  },
  BILLING_PLAN_NO_PRICE_BRACKETS: {
    errorCode: 14122,
    errorMessage: t("Plan has no price brackets configured"),
    isDynamic: false,
  },
  BILLING_SUBSCRIPTION_CANNOT_PAUSE_ENDED: {
    errorCode: 14124,
    errorMessage: t("Cannot pause a cancelled or expired subscription"),
    isDynamic: false,
  },
  BILLING_QUANTITY_CHANGE_REQUIRES_ACTIVE: {
    errorCode: 14125,
    errorMessage: t("Can only change quantity for active subscriptions"),
    isDynamic: false,
  },
  BILLING_NO_PLAN_IN_LINE_ITEMS: {
    errorCode: 14126,
    errorMessage: t("No plan found in subscription lineItems"),
    isDynamic: false,
  },
  BILLING_ADDON_REQUIRES_ACTIVE_SUBSCRIPTION: {
    errorCode: 14127,
    errorMessage: t("Can only add addons to active subscriptions"),
    isDynamic: false,
  },
  BILLING_ADDON_NOT_ON_SUBSCRIPTION: {
    errorCode: 14128,
    errorMessage: t("Addon not found in subscription"),
    isDynamic: false,
  },
  BILLING_NO_ACTIVE_PLANS_FOR_TRIAL: {
    errorCode: 14129,
    errorMessage: t("No active plans found to create trial subscription"),
    isDynamic: false,
  },
  // 14137-14138: the two gateway calls whose own error text (razorpayOrder.error / refundResult.error) is remote copy — it rides in params under UNEXPECTED_ERROR_WITH_MESSAGE when present, and these name the refusal when it is not.
  BILLING_PAYMENT_ORDER_CREATE_FAILED: {
    errorCode: 14137,
    errorMessage: t("Failed to create payment order"),
    isDynamic: false,
  },
  BILLING_REFUND_INITIATE_FAILED: {
    errorCode: 14138,
    errorMessage: t("Failed to initiate refund"),
    isDynamic: false,
  },
  // 14139: the Dodo checkout failure. The raw gateway response body was being shipped to the user; it now goes to logs only.
  BILLING_DODO_CHECKOUT_CREATE_FAILED: {
    errorCode: 14139,
    errorMessage: t("The payment checkout could not be created. Please try again."),
    isDynamic: false,
  },
} satisfies Record<string, ErrorCodeMapperEntry>;
